import React, { useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom'; // add at top

import tpo from '../../../assets/ColumbusPage/Commercial Roof/TPO_Roofing_Contactor_Columbus_Ohio.jpg';
import pvc from '../../../assets/ColumbusPage/Commercial Roof/PVC_Roofing_Contactor_Columbus_Ohio.jpg';
import epdm from '../../../assets/ColumbusPage/Commercial Roof/EPDM_Roofing_Contactor_Columbus_Ohio_Ultimatesroofing.jpg';
import coatings from '../../../assets/ColumbusPage/Commercial Roof/Roof_Coatings_Maintenance_Columbus_Ohio.jpg';
import repairs from '../../../assets/ColumbusPage/Commercial Roof/Commercial_Roof_Repairs.jpg';
import metal from '../../../assets/ColumbusPage/Commercial Roof/Commercial_Metal_Roofing.jpg';

import '../all/Main.css';
import './RoofingSection.css';

import View from './View';
import Badge from '../ServiceSection/Badge';

const SERVICES = [
  {
    title: "TPO Roofing",
    description: "Energy-efficient TPO roofing reducing cooling costs for commercial buildings.",
    image: tpo,
    tag: "Commercial"
  },
  {
    title: "EPDM Roofing",
    description: "Flexible EPDM rubber roofing offering excellent weather and UV resistance.",
    image: epdm,
    tag: "Commercial"
  },
  {
    title: "PVC Roofing",
    description: "High-performance PVC roofing ideal for demanding commercial environments.",
    image: pvc,
    tag: "Commercial"
  },
  {
    title: "Roof Coatings & Maintenance",
    description: "Protective coatings extending roof life and reducing long-term repair costs.",
    image: coatings,
    tag: "Commercial"
  },
  {
    title: "Commercial Metal Roofing",
    description: "Long-lasting metal roofing for strength, low maintenance, and energy savings.",
    image: metal,
    tag: "Commercial"
  },
  {
    title: "Commercial Roof Repairs",
    description: "Fast, professional repairs minimizing business disruption and preventing damage.",
    image: repairs,
    tag: "Commercial"
  }
];

const COLORS = {
  primary: '#3D3B6D',
  accent: '#b22335',
  border: '#e8e8ef',
  cardBg: '#f8f9fa',
  overlay: 'rgba(61, 59, 109, 0.65)'
};

const ScrollReveal = ({ children, delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const directionMap = {
    up: { y: 60 },
    left: { x: 60 },
    right: { x: -60 }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...directionMap[direction] }}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.7, delay }}
    >
      {children}
    </motion.div>
  );
};

const ParallaxImage = ({ src, alt }) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);

  return (
    <div ref={ref} className="position-relative overflow-hidden flex-shrink-0" style={{ height: '240px' }}>
      <motion.img
        src={src}
        alt={alt}
        className="w-100 h-100"
        style={{ objectFit: 'cover', y }}
      />
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background: `linear-gradient(180deg, ${COLORS.overlay} 0%, rgba(26,26,46,.45) 100%)`
        }}
      />
    </div>
  );
};

const ServiceCard = ({ service, index }) => {
  const isEven = index % 2 === 0;

  return (
    <motion.div whileHover={{ y: -10, scale: 1.02 }} className="h-100 d-flex flex-column w-100">
      <div
        className="h-100 position-relative overflow-hidden d-flex flex-column"
        style={{
          borderRadius: '16px',
          backgroundColor: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`
        }}
      >
        <ParallaxImage src={service.image} alt={service.title} />

        <div className="position-absolute" style={{ top: '14px', left: '14px', zIndex: 2 }}>
          <span
            className="comm-badge"
            style={{
              background: isEven ? COLORS.accent : COLORS.primary
            }}
          >
            {service.tag}
          </span>
        </div>

        <div className="p-4 d-flex flex-column flex-grow-1">
          <h3 className=" m-0 mb-2 comm-card-title">
            {service.title}
          </h3>

          <p className="comm-card-desc flex-grow-1">
            {service.description}
          </p>

          <Link to="/commercial-roofing" className="comm-card-link mt-auto">
            Learn More
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="ms-2"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div
          className="position-absolute bottom-0 start-0"
          style={{
            height: '3px',
            width: '100%',
            background: `linear-gradient(90deg, ${isEven ? COLORS.accent : COLORS.primary}, transparent)`
          }}
        />
      </div>
    </motion.div>
  );
};

const RoofingSection = () => {
  return (
    <section className="roofing-commercial-section cs-section">
      <Container>
        <div className="">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <ScrollReveal direction="left">
                <div className="cs-badge">
                  <Badge text={"Commercial Roofing"} />
                </div>
              </ScrollReveal>

              <ScrollReveal direction="left" delay={0.1}>
                <h2 className="comm-title cs-title">
                  Columbus Commercial <span>Roofing Solutions</span>
                </h2>
              </ScrollReveal>

              <ScrollReveal direction="left" delay={0.2}>
                <p className="cs-desc comm-desc">
                  Trusted commercial roofing services in Columbus for flat roofs, TPO, EPDM, repairs, and replacements.
                </p>
              </ScrollReveal>
            </div>

            <View />
          </div>
        </div>

        <Row className="g-4 cs-content">
          {SERVICES.map((service, index) => (
            <Col lg={4} md={6} key={index} className="d-flex flex-column">
              <ServiceCard service={service} index={index} />
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default RoofingSection;
